import { VerifyEmail } from '@components/Banners/VerifyEmail';
import { AccountPage } from '@pages/Account/AccountPage';
import { AuthBox } from '@pages/Auth/AuthBox';
import { ConfirmEmailPage } from '@pages/Auth/ConfirmEmailPage/ConfirmEmailPage';
import { LoginForm } from '@pages/Auth/LoginPage/LoginForm';
import { MagicLinkPage } from '@pages/Auth/MagicLinkPage/MagicLinkPage';
import { PasswordResetForm } from '@pages/Auth/PasswordResetPage/PasswordResetForm';
import { PasswordResetRequestForm } from '@pages/Auth/PasswordResetPage/PasswordResetRequestForm';
import { RegisterForm } from '@pages/Auth/RegisterPage/RegisterForm';
import { BevelButtonPage } from '@pages/Debug/BevelButtonPage';
import { MobileDebugOffPage, MobileDebugOnPage } from '@pages/Debug/MobileDebugPage';
import { ToastDebugPage } from '@pages/Debug/ToastDebugPage';
import { UpdateCreditsLeftPage } from '@pages/Debug/UpdateCreditsLeftPage';
import { LibrariesPage } from '@pages/Libraries/LibrariesPage';
import { PaymentReturnPage } from '@pages/Payment/PaymentReturnPage';
import { PaywallPage } from '@pages/Paywall/PaywallPage';
import { PodcastsPage } from '@pages/Podcasts/PodcastsPage';
import { PricingPage } from '@pages/Pricing/PricingPage';
import { ComposePage } from '@pages/Schedule/ComposePage';
import { PostPage } from '@pages/Schedule/PostPage';
import { SchedulePage } from '@pages/Schedule/SchedulePage';
import { SearchPage } from '@pages/Search/SearchPage/SearchPage';
import { SettingsPage } from '@pages/Settings/Settings';
import { WorkspacePage } from '@pages/Workspace/WorkspacePage';
import { IntercomProvider } from '@stores/Intercom';
import { loginPreferences, PreferredLoginMethod } from '@stores/LoginPreferences';
import { trackingStore } from '@stores/Tracking';
import { UIPreferencesProvider } from '@stores/UIPreferences';
import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, useNavigate, useSearchParams } from 'react-router-dom';
import { App, WithAuthHero, WithNav, WithoutUser, WithToaster, WithUser } from './App';
import { LibraryPageWrapper } from './LibraryPageWrapper';
import { WithLibraryPage } from './WithLibraryPage';

const PasswordResetFormWithCode = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  return code ? <PasswordResetForm code={code} /> : <PasswordResetRequestForm />;
};

// /login/with-password and /login/magic-link don't render a form — they record
// the chosen method as this device's preference, then bounce to /login (which
// renders the matching form). This keeps the visible login URL canonical at
// /login while letting the toggle and any deep links pick the method.
//
// The preference is set and the navigation issued from the same effect, in that
// order, so /login reads the new value on its first render (no flash of the
// previous form). Doing this in an effect (not during render) avoids mutating
// the observable while the LoginForm observer we're navigating away from is
// still rendering. Email/redirect query params are carried through.
const LoginMethodRedirect = ({ method }: { method: PreferredLoginMethod }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => {
    loginPreferences.setPreferredMethod(method);
    trackingStore.loginMethodSelected(method, 'login');
    const qs = searchParams.toString();
    navigate(qs ? `/login?${qs}` : '/login', { replace: true });
  }, [method, navigate, searchParams]);
  return null;
};

// Wrapper component that provides UIPreferences
const WithUIPreferences = ({ children }: { children: React.ReactNode }) => {
  return <UIPreferencesProvider>{children}</UIPreferencesProvider>;
};

// example of lazy loading a page
// should be used for very heavy pages
// otherwise, chunking will try its best to split JS modules
// import { lazy } from "react";
// const LazySearchPage = lazy(() => import('@pages/Search/SearchPage/SearchPage').then(x => ({ default: x.SearchPage })));

// Story Atlas is a self-contained full-screen feature — lazy-load it so its
// chart/scene machinery stays out of the main bundle.
const LazyAtlasPage = lazy(() => import('@pages/Atlas/AtlasPage'));

function DeferredNavigation({ to }: { to: string }) {
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      navigate(to);
    }, 1);
  }, []);

  return null;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '/pricing',
        element: (
          <WithUIPreferences>
            <PricingPage />
          </WithUIPreferences>
        ),
      },
      {
        path: '/',
        element: (
          <WithUIPreferences>
            <IntercomProvider>
              <WithToaster>
                <WithUser />
              </WithToaster>
            </IntercomProvider>
          </WithUIPreferences>
        ),
        children: [
          {
            path: '/debug/update-credits-left',
            element: <UpdateCreditsLeftPage />,
          },
          {
            path: '/debug/mobile/on',
            element: <MobileDebugOnPage />,
          },
          {
            path: '/debug/mobile/off',
            element: <MobileDebugOffPage />,
          },
          {
            path: '/debug/bevel-button',
            element: <BevelButtonPage />,
          },
          {
            path: '/debug/toasts',
            element: <ToastDebugPage />,
          },
          {
            // Story Atlas: full-screen narrative visualization of a library.
            // Sits beside WithNav (like the debug pages) so it owns the whole
            // viewport — it renders its own chrome and back-to-library exit.
            path: '/atlas/:libraryId',
            element: (
              <Suspense fallback={null}>
                <LazyAtlasPage />
              </Suspense>
            ),
          },
          {
            // The paywall. WithUser sends a walled account here; it owns the whole
            // viewport like the debug pages, so it sits beside WithNav.
            path: '/subscribe',
            element: <PaywallPage />,
          },
          {
            // Stripe's return URLs (the backend's STRIPE_PAYMENT_*_PATH defaults).
            path: '/payment/success',
            element: <PaymentReturnPage outcome="success" />,
          },
          {
            path: '/payment/failed',
            element: <PaymentReturnPage outcome="failed" />,
          },
          {
            path: '/',
            element: <WithNav />,
            children: [
              {
                path: '/settings/:tab?',
                element: <SettingsPage />,
              },
              {
                path: '/account',
                element: <AccountPage />,
              },
              {
                path: '/',
                children: [
                  {
                    path: '/',
                    // old code:
                    // element: <Navigate to="/workspace" />,
                    // fixme: to whoever will try to fix this
                    //        <WithoutUser /> component redirect us here with navigate
                    //        this happens when we transition from not logged in to logged in
                    //        we get here via a navigate('/' + ...)
                    //        and if we were to navigate again to /workspace in the mount of the component
                    //        it just renders the component twice without actually navigating
                    //        this is a workaround to defer the navigation to the next JS tick.
                    //        The theory is that the router has an interal state that is not cleared from the first navigation
                    //        and consecutive navigations don't work properly.

                    // extra context:
                    //        The issue occurs only the first time we navigate to the website.
                    //        (i.e. fresh incognito window)
                    //        We cannot repro once we've logged in at least once,
                    //        so there must be some lingering localStorage state or registration of components or even caching??? that silently fixes it
                    element: <DeferredNavigation to="/workspace" />,
                  },
                  {
                    path: '/search',
                    element: <SearchPage />,
                  },
                  {
                    path: '/workspace',
                    element: <WorkspacePage />,
                  },
                  {
                    path: '/podcasts',
                    element: <PodcastsPage />,
                  },
                  {
                    path: '/schedule',
                    element: <SchedulePage />,
                  },
                  {
                    path: '/schedule/create/:renderId',
                    element: <ComposePage />,
                  },
                  {
                    path: '/schedule/post/:postId',
                    element: <PostPage />,
                  },
                  {
                    path: '/libraries',
                    element: <LibrariesPage />,
                  },
                  {
                    path: '/library/:id',
                    element: <WithLibraryPage />,
                    children: [
                      {
                        path: '/library/:id',
                        element: <LibraryPageWrapper />,
                      },
                      {
                        path: '/library/:id/media/:mediaId',
                        element: <LibraryPageWrapper />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/verify-email',
        element: (
          <AuthBox>
            <VerifyEmail />
          </AuthBox>
        ),
      },
      {
        path: '/confirm-email',
        element: (
          <AuthBox>
            <ConfirmEmailPage />
          </AuthBox>
        ),
      },
      {
        path: '/magic-link',
        element: (
          <AuthBox>
            <MagicLinkPage />
          </AuthBox>
        ),
      },
      {
        path: '/reset-password',
        element: (
          <AuthBox>
            <PasswordResetFormWithCode />
          </AuthBox>
        ),
      },
      {
        path: '/',
        element: <WithoutUser />,
        children: [
          {
            path: '/',
            element: <WithAuthHero />,
            children: [
              {
                path: '/login',
                element: <LoginForm />,
              },
              {
                path: '/login/with-password',
                element: <LoginMethodRedirect method="password" />,
              },
              {
                path: '/login/magic-link',
                element: <LoginMethodRedirect method="magic-link" />,
              },
              {
                path: '/register',
                element: <LoginForm forceMagicLink />,
              },
              {
                path: '/register/with-password',
                element: <RegisterForm />,
              },
            ],
          },
        ],
      },
    ],
  },
]);
